import { useEffect, useRef, useState } from 'react';
import { searchAddresses } from '../utils/api';
import './AddressAutocompleteField.css';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 350;

const AddressAutocompleteField = ({
  value,
  onChange,
  onBlur,
  id = 'adresa',
  name = 'adresa',
  label = 'Adresa',
  required = false,
  title,
  errorMessage = '',
  hasError = false,
  className = '',
}) => {
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasTyped, setHasTyped] = useState(false);
  const wrapperRef = useRef(null);
  const requestIdRef = useRef(0);
  const suppressNextLookupRef = useRef(false);

  const emitChange = (nextValue) => {
    if (typeof onChange === 'function') {
      onChange({
        target: {
          name,
          value: nextValue,
        },
      });
    }
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const selectSuggestion = (item) => {
    suppressNextLookupRef.current = true;
    emitChange(item.label);
    setResults([]);
    closeDropdown();

    if (typeof onBlur === 'function') {
      onBlur({
        target: {
          name,
          value: item.label,
        },
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const query = value.trim();

    if (suppressNextLookupRef.current) {
      suppressNextLookupRef.current = false;
      return;
    }

    if (!hasTyped || query.length < MIN_QUERY_LENGTH) {
      setIsLoading(false);
      setResults([]);
      closeDropdown();
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setIsLoading(true);

      try {
        const response = await searchAddresses(query);
        if (requestId !== requestIdRef.current) return;

        const items = Array.isArray(response.data?.data) ? response.data.data : [];
        setResults(items);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        setIsOpen(true);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setHighlightedIndex(-1);
        setIsOpen(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [hasTyped, value]);

  const handleInputChange = (event) => {
    setHasTyped(true);
    onChange?.(event);

    if (!isOpen && event.target.value.trim().length >= MIN_QUERY_LENGTH) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = (event) => {
    window.setTimeout(() => {
      closeDropdown();
    }, 120);

    onBlur?.(event);
  };

  const handleInputFocus = () => {
    const query = value.trim();
    if (query.length >= MIN_QUERY_LENGTH && results.length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event) => {
    if (!isOpen) {
      if (event.key === 'ArrowDown' && results.length > 0) {
        event.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (results.length === 0) return -1;
        return prev >= results.length - 1 ? 0 : prev + 1;
      });
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (results.length === 0) return -1;
        return prev <= 0 ? results.length - 1 : prev - 1;
      });
    }

    if (event.key === 'Enter' && highlightedIndex >= 0 && results[highlightedIndex]) {
      event.preventDefault();
      selectSuggestion(results[highlightedIndex]);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown();
    }
  };

  const groupClasses = [
    'form-group-floating',
    'address-autocomplete-field',
    value ? 'has-value' : '',
    hasError ? 'has-error' : '',
    isOpen ? 'has-open-dropdown' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const showEmptyState = isOpen && !isLoading && hasTyped && value.trim().length >= MIN_QUERY_LENGTH && results.length === 0;

  return (
    <div className={groupClasses} ref={wrapperRef}>
      <input
        type="text"
        name={name}
        id={id}
        className="form-input-floating address-autocomplete-input"
        value={value}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder=" "
        title={title}
        autoComplete="street-address"
        required={required}
      />
      <label htmlFor={id} className="form-label-floating">{label}</label>

      {isOpen && (
        <div className="address-autocomplete-dropdown" role="listbox" aria-label="Navrhy adresy">
          {isLoading && (
            <div className="address-autocomplete-state">Vyhľadávam adresy...</div>
          )}

          {!isLoading && results.map((item, index) => (
            <button
              type="button"
              key={`${item.label}-${index}`}
              className={`address-autocomplete-option ${highlightedIndex === index ? 'is-active' : ''}`}
              onMouseDown={(event) => {
                event.preventDefault();
                selectSuggestion(item);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              role="option"
              aria-selected={highlightedIndex === index}
            >
              <span className="address-autocomplete-option-label">{item.label}</span>
              {item.secondary && (
                <span className="address-autocomplete-option-meta">{item.secondary}</span>
              )}
            </button>
          ))}

          {showEmptyState && (
            <div className="address-autocomplete-state">
              Žiadne výsledky neboli nájdené pre zadanú adresu.
            </div>
          )}
        </div>
      )}

      {errorMessage && (
        <span className="field-error">{errorMessage}</span>
      )}
    </div>
  );
};

export default AddressAutocompleteField;
